import { magic } from '@gig/magic-sdk';
import VueRouter, { Route } from 'vue-router';
import { RouteName } from '../../../routes/routes.constants';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import {
    GamingLimitType,
    GamingLimitDuration,
} from '@gig/magic-sdk/dist/adapter/responsiblegaming/setUserLimits';
import { ipCountryQuery } from '@gig/magic-common';

interface IRequiresDepositLimitsMeta {
    requireDepositLimit?: {
        countries: Country[];
    };
}

/**
 * Determines that this route should require deposit limits
 * set before allowing access. Blocks access only when the
 * countries match the countries passed through.
 * @param countries The countries to apply the block to
 */
export function requireDepositLimitsGuard(
    countries: Country[],
): IRequiresDepositLimitsMeta {
    return {
        requireDepositLimit: {
            countries: countries || [],
        },
    };
}

// The durations required to be set for the limits check
const REQUIRED_DURATIONS = [
    GamingLimitDuration.ONE_DAY,
    GamingLimitDuration.ONE_WEEK,
    GamingLimitDuration.ONE_MONTH,
];

export function initDepositLimitGuard(router: VueRouter) {
    router.beforeResolve(
        async (
            {
                meta,
                fullPath,
                params,
            }: Route & { meta?: IRequiresDepositLimitsMeta },
            _,
            next,
        ) => {
            const user = magic.session.getUser();

            if (!meta || !meta.requireDepositLimit || !user) {
                return next();
            }

            const ipCountry = user.country || (await ipCountryQuery());
            const blockedCountries = meta.requireDepositLimit.countries || [];

            if (!blockedCountries.includes(ipCountry)) {
                return next();
            }

            // Check if our user has *all* deposit limits set
            const limits =
                await magic.methods.responsibleGaming.getUserLimits();

            const filtered = limits.activeLimits.filter(
                ({ amount, limitTypeId }) => {
                    return limitTypeId === GamingLimitType.DEPOSIT && amount;
                },
            );

            const hasAll = REQUIRED_DURATIONS.every((id) => {
                return filtered.find(({ durationId }) => durationId === id);
            });

            if (hasAll) {
                return next();
            }

            next({
                name: RouteName.SET_DEPOSIT_LIMITS,
                params,
                query: {
                    backTo: fullPath,
                },
            });
        },
    );
}
